import { useEffect, useMemo } from 'react';

import Button from 'components/Button';
import ComponentPlaceholder from 'components/ComponentPlaceholder';
import { LayoutContainer } from 'components/Layout';
import LoadMoreList from 'components/LoadMoreList';
import Meter from 'components/Meter';
import { MicroContentMedia } from 'components/MicroContent';
import SwipeWrapper from 'components/SwipeWrapper';
import Text from 'components/Text';
import {
	INSPIRATION_OVERVIEW_OFFSET_QUERY_PARAM,
	INSPIRATION_OVERVIEW_PAGE_SIZE_QUERY_PARAM,
	INSPIRATION_OVERVIEW_TAG_QUERY_PARAM,
} from 'constants/queryParams';
import { withRequiredProps } from 'hoc';
import { useIsEditing, usePagination, useSafeFieldValues } from 'hooks';
import { useSearchParams } from 'hooks/navigation';
import type {
	Field,
	InspirationPage,
	SitecoreBlockProps,
	Tag,
} from 'models/sitecore';
import { getEditorMargin } from 'utils/business-logic';
import { filterTruthy } from 'utils/collection';
import { empty, getAsset, is } from 'utils/helpers';
import { useI18n } from 'utils/i18n';
import { log } from 'utils/log';

interface Fields {
	allTagsButtonText?: Field<string>;
	hasNextPage: boolean;
	heading?: Field<string>;
	items: InspirationPage[];
	loadMoreButtonText?: Field<string>;
	nextPageOffset: number;
	tags: Tag[];
	total: number;
}

interface Props extends SitecoreBlockProps {
	fields: Fields;
}

function InspirationOverview({ fields: initialFields, params }: Props) {
	const texts = useSafeFieldValues(initialFields);
	const { t, tPlural } = useI18n();
	const isEditing = useIsEditing();
	const query = useSearchParams();

	const urlTags = query.getAll('io-tag').filter(is.truthy);

	const {
		component: inspirationFields,
		isLoading: isLoadingMoreItems,
		items: rawItems,
		loadMore: loadMoreItems,
		queryVarItems,
		updateQueryVars,
	} = usePagination<InspirationPage, Fields>({
		initialComponent: initialFields,
		initialItems: initialFields.items,
		initialNextPageOffset: initialFields.nextPageOffset,
		initialQueryVars: urlTags.map((tag) => [
			INSPIRATION_OVERVIEW_TAG_QUERY_PARAM,
			tag,
		]),
		itemsKey: 'items',
		offsetQueryVarName: INSPIRATION_OVERVIEW_OFFSET_QUERY_PARAM,
		pageSizeQueryVarName: INSPIRATION_OVERVIEW_PAGE_SIZE_QUERY_PARAM,
		placeholderComponentName: 'InspirationOverview',
	});

	const tags = inspirationFields?.tags ?? empty.array;
	const activeTagIds = queryVarItems
		.filter(([key]) => key === INSPIRATION_OVERVIEW_TAG_QUERY_PARAM)
		.map(([, val]) => val);
	const items = filterTruthy(
		rawItems.map((item) => ({
			href: item.url,
			imgSrc: getAsset('Templated 2:1', item.fields?.landscapeImage)?.src,
			key: item.id,
			text: item.fields?.navigationTitle?.value,
		})),
		'href',
		'imgSrc',
		'text',
	);

	const itemsWithMissingData = useMemo(
		() =>
			rawItems
				.filter(
					(item) =>
						!item.url ||
						!item.fields?.navigationTitle?.value ||
						!getAsset('Templated 2:1', item.fields?.landscapeImage)?.src,
				)
				.map((item) => ({
					id: item.id,
					displayName: item.displayName,
				})),
		[rawItems],
	);

	useEffect(() => {
		if (is.arrayWithLength(itemsWithMissingData)) {
			log.warning(
				'The total number of items is greater than the number of visible items',
				itemsWithMissingData,
			);
		}
	}, [itemsWithMissingData]);

	// Remove the missing items from the total for the last page, when we know the total missing
	// to make the meter more accurate.
	const totalItemsCount = inspirationFields?.hasNextPage
		? inspirationFields?.total
		: (inspirationFields?.total ?? 0) - itemsWithMissingData.length;
	const visibleItemsCount = items.length;

	if (isEditing && visibleItemsCount === 0 && !initialFields.heading?.value) {
		return (
			<ComponentPlaceholder
				componentName="InspirationOverview"
				className={getEditorMargin(params)}
			/>
		);
	}

	const toggleTag = (tagId: string) => {
		updateQueryVars((vars) => {
			if (vars.has(INSPIRATION_OVERVIEW_TAG_QUERY_PARAM, tagId)) {
				vars.delete(INSPIRATION_OVERVIEW_TAG_QUERY_PARAM, tagId);
			} else {
				vars.add([INSPIRATION_OVERVIEW_TAG_QUERY_PARAM, tagId]);
			}
		});
	};

	const clearAllTags = () => {
		updateQueryVars((vars) => {
			vars.delete(INSPIRATION_OVERVIEW_TAG_QUERY_PARAM);
		});
	};

	const isTagActive = (tagId: string) => {
		if (!tagId && activeTagIds.length === 0) {
			return true;
		}
		return activeTagIds.includes(tagId);
	};

	const filterTags: Tag[] = [
		{ id: '', name: texts.allTagsButtonText },
		...tags,
	];

	return (
		<LayoutContainer
			id={params?.anchor}
			outerClassName={getEditorMargin(params)}
		>
			<Text as={params?.heading ?? 'h2'} field={inspirationFields?.heading} />

			<SwipeWrapper
				activeClassName="mt-2 mb-4 sm:hidden"
				inactiveClassName="mt-4 mb-6 flex flex-wrap gap-4 max-sm:hidden"
				pullGutters
			>
				{filterTags.map((tag) => (
					<li key={`${tag.name}-${tag.id}`}>
						<Button
							variant={isTagActive(tag.id) ? 'branded' : 'secondary'}
							aria-pressed={isTagActive(tag.id)}
							onClick={() => {
								if (tag.id) {
									toggleTag(tag.id);
								} else {
									clearAllTags();
								}
							}}
						>
							{tag.name}
						</Button>
					</li>
				))}
			</SwipeWrapper>

			<LoadMoreList
				isLoading={isLoadingMoreItems}
				onLoadMoreClick={loadMoreItems}
				hasLoadMoreButton={inspirationFields?.hasNextPage}
				loadedItemFocusTarget="first-interactive"
				buttonAlignment="center"
				buttonClassName="mt-4 max-sm:w-full sm:min-w-72"
				itemCountScreenReaderText={tPlural(
					'inspiration_item_count_text',
					visibleItemsCount,
				)}
				buttonText={texts.loadMoreButtonText}
				listTag="ul"
				listClassName="grid grid-cols-1 items-start gap-4 sm:grid-cols-2 md:grid-cols-4 md:gap-6"
				afterListContent={
					<Meter
						alignment="center"
						className="mt-14"
						current={visibleItemsCount}
						max={totalItemsCount}
						labelHasProgress
						label={t('inspiration_overview_page_indicator_text', {
							numShown: visibleItemsCount,
							numTotal: totalItemsCount,
						})}
					/>
				}
			>
				{items.map((item) => (
					<li key={item.key} className="col-span-1">
						<MicroContentMedia
							href={item.href}
							imgSrc={item.imgSrc}
							imgWidth={393}
							imgHeight={197}
							text={item.text}
						/>
					</li>
				))}
			</LoadMoreList>
		</LayoutContainer>
	);
}
InspirationOverview.displayName = 'InspirationOverview';

export default withRequiredProps(InspirationOverview, 'fields');
